import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert, Table } from "reactstrap";
import { toast } from "react-toastify";

const TenantAccessBlockedModal = ({ isOpen, toggle, tenantData }) => {
  const [view, setView] = useState("main"); // "main" | "pix" | "receipt"
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Reset view when modal closes/opens
  useEffect(() => {
    if (!isOpen) {
      setView("main");
      setSelectedInvoice(null);
    }
  }, [isOpen]);

  if (!tenantData) return null;

  const isOwnerOrAdmin = tenantData.role === "owner" || tenantData.role === "super_admin";
  const formatIsoDatePtBr = (isoStr) => {
    if (!isoStr) return "-";
    try {
      const date = new Date(isoStr);
      return date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
    } catch {
      return isoStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <span className="badge bg-success-subtle text-success font-size-12 px-2 py-1">Pago</span>;
      case "pending":
        return <span className="badge bg-warning-subtle text-warning font-size-12 px-2 py-1">Pendente</span>;
      case "rejected":
        return <span className="badge bg-danger-subtle text-danger font-size-12 px-2 py-1">Recusado</span>;
      default:
        return <span className="badge bg-secondary-subtle text-secondary font-size-12 px-2 py-1">{status || "Desconhecido"}</span>;
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size={isOwnerOrAdmin && view === "main" ? "lg" : "md"} centered>
      <ModalHeader toggle={toggle} className="border-bottom-0 pb-0">
        {view === "main" && (
          <span className="fw-bold text-dark font-size-16">
            {isOwnerOrAdmin ? "⚠️ Assinatura Pendente — NeoApp" : "⚠️ Acesso Suspenso"}
          </span>
        )}
        {view === "pix" && (
          <span className="fw-bold text-dark font-size-16">
            <i className="mdi mdi-arrow-left me-2 cursor-pointer" onClick={() => setView("main")} />
            Pagamento via Pix
          </span>
        )}
        {view === "receipt" && (
          <span className="fw-bold text-dark font-size-16">
            <i className="mdi mdi-arrow-left me-2 cursor-pointer" onClick={() => setView("main")} />
            Comprovante de Pagamento
          </span>
        )}
      </ModalHeader>

      <ModalBody className="pt-2">
        {/* VIEW: MAIN (Employee Block or Owner Invoices List) */}
        {view === "main" && (
          <div>
            {!isOwnerOrAdmin ? (
              // Case: Employee/Collaborator
              <div className="text-center py-3">
                <div className="mb-3">
                  <i className="mdi mdi-lock-outline text-danger" style={{ fontSize: "3.5rem" }} />
                </div>
                <h5 className="text-dark fw-bold mb-3">Acesso Bloqueado</h5>
                <Alert color="danger" fade={false} className="text-start font-size-13 py-3 border-0 rounded-3 bg-danger-subtle text-danger">
                  Ocorreu um problema com o acesso da sua empresa <strong>{tenantData.tenantName}</strong>. 
                  Por favor, consulte o proprietário ou a gestão da academia para regularizar a assinatura.
                </Alert>
              </div>
            ) : (
              // Case: Owner or Super Admin
              <div>
                <Alert color="danger" fade={false} className="border-0 rounded-3 bg-danger-subtle text-danger py-3 font-size-13 mb-4">
                  <i className="mdi mdi-alert-circle-outline me-2 font-size-15 align-middle" />
                  {tenantData.blockMessage || "Seu período de teste ou assinatura expirou. Regularize o faturamento abaixo para reativar o acesso imediatamente."}
                </Alert>

                <div className="mb-3">
                  <h6 className="fw-semibold text-dark font-size-14 mb-2">Histórico de Faturas:</h6>
                  {tenantData.invoices && tenantData.invoices.length > 0 ? (
                    <div className="table-responsive rounded border border-light">
                      <Table className="align-middle table-nowrap table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="font-size-12">Emissão</th>
                            <th className="font-size-12">Plano</th>
                            <th className="font-size-12">Valor</th>
                            <th className="font-size-12">Status</th>
                            <th className="font-size-12 text-center">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tenantData.invoices.map((inv) => {
                            const isApproved = inv.paymentStatus === "approved";
                            const isPending = inv.paymentStatus === "pending";

                            return (
                              <tr key={inv.id}>
                                <td className="font-size-13">{formatIsoDatePtBr(inv.createdAt)}</td>
                                <td>
                                  <span className="font-size-13 fw-semibold text-dark">{inv.planTitle || "Mensalidade NeoApp"}</span>
                                  <div className="text-muted font-size-11">{inv.planCode || "SaaS"}</div>
                                </td>
                                <td className="font-size-13 font-weight-bold text-dark">
                                  {Number(inv.amount || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                                </td>
                                <td>{getStatusBadge(inv.paymentStatus)}</td>
                                <td className="text-center">
                                  <div className="d-flex justify-content-center gap-2">
                                    {isApproved && (
                                      <Button
                                        color="success"
                                        size="sm"
                                        outline
                                        className="py-1 px-2"
                                        title="Recibo"
                                        onClick={() => {
                                          setSelectedInvoice(inv);
                                          setView("receipt");
                                        }}
                                      >
                                        <i className="mdi mdi-receipt font-size-14" />
                                      </Button>
                                    )}
                                    {isPending && (inv.qr_code || inv.qr_code_base64) && (
                                      <Button
                                        color="primary"
                                        size="sm"
                                        className="py-1 px-2"
                                        title="Pagar Pix"
                                        onClick={() => {
                                          setSelectedInvoice(inv);
                                          setView("pix");
                                        }}
                                      >
                                        <i className="mdi mdi-qrcode-scan font-size-14" />
                                      </Button>
                                    )}
                                    {isPending && inv.ticket_url && (
                                      <a
                                        href={inv.ticket_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn btn-primary btn-sm py-1 px-2"
                                        title="Visualizar Boleto"
                                      >
                                        <i className="mdi mdi-barcode font-size-14" />
                                      </a>
                                    )}
                                    {!isApproved && !isPending && (
                                      <span className="text-muted font-size-11">Nenhuma ação</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-light rounded-3 text-muted font-size-13">
                      Nenhuma fatura encontrada. Caso tenha acabado de assinar, aguarde alguns instantes.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* VIEW: PIX DETAILS */}
        {view === "pix" && selectedInvoice && (
          <div className="text-center py-3">
            {(selectedInvoice.qr_code_base64 || selectedInvoice.qr_code) && (
              <div className="mb-3">
                <p className="fw-semibold font-size-13 text-muted mb-2">Escaneie o QR Code abaixo pelo app do seu banco:</p>
                <div style={{ background: "white", padding: "12px", borderRadius: "12px", display: "inline-block", border: "1px solid #eee" }}>
                  <img
                    src={selectedInvoice.qr_code_base64 && selectedInvoice.qr_code_base64.length > 200
                      ? (selectedInvoice.qr_code_base64.startsWith("data:") ? selectedInvoice.qr_code_base64 : `data:image/png;base64,${selectedInvoice.qr_code_base64}`)
                      : `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(selectedInvoice.qr_code)}`
                    }
                    alt="QR Code Pix"
                    style={{ width: "180px", height: "180px", display: "block" }}
                  />
                </div>
              </div>
            )}
            
            {selectedInvoice.qr_code && (
              <div className="px-2">
                <p className="fw-semibold font-size-13 text-muted mb-1">Ou copie a chave copia e cola abaixo:</p>
                <div 
                  className="p-3 bg-light rounded text-break font-size-11 mb-3 text-muted text-start" 
                  style={{ border: "1px solid #eef", maxHeight: "80px", overflowY: "auto", userSelect: "all", fontFamily: "monospace" }}
                >
                  {selectedInvoice.qr_code}
                </div>
                <div className="d-flex gap-2">
                  <Button
                    color="secondary"
                    className="w-50 py-2 font-size-13"
                    onClick={() => setView("main")}
                  >
                    Voltar
                  </Button>
                  <Button
                    color="primary"
                    className="w-50 py-2 font-size-13 fw-semibold"
                    onClick={() => {
                      navigator.clipboard.writeText(selectedInvoice.qr_code);
                      toast.success("Chave Pix copiada para a área de transferência!");
                    }}
                  >
                    <i className="mdi mdi-content-copy me-1" />
                    Copiar Pix
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* VIEW: RECEIPT DETAILS */}
        {view === "receipt" && selectedInvoice && (
          <div>
            <div className="text-center mb-4 pt-2">
              <i className="mdi mdi-check-circle-outline text-success" style={{ fontSize: "2.8rem" }} />
              <h5 className="mt-2 text-dark fw-bold">Pagamento Confirmado</h5>
              <p className="text-muted font-size-12">Plataforma NeoApp SaaS</p>
            </div>
            <div className="border border-light rounded-3 p-3 bg-light font-size-13 text-dark mb-4">
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Academia:</span>
                <strong className="text-dark">{tenantData.tenantName}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Plano:</span>
                <strong className="text-dark">{selectedInvoice.planTitle || "Assinatura Mensal"}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Código Fatura:</span>
                <span className="text-muted text-break">{selectedInvoice.externalReference || selectedInvoice.id}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Data Pagamento:</span>
                <strong className="text-dark">{formatIsoDatePtBr(selectedInvoice.createdAt)}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Método:</span>
                <strong className="text-dark">{String(selectedInvoice.paymentMethodId || "Cartão").toUpperCase()}</strong>
              </div>
              <hr className="my-2 border-top border-light-dark" />
              <div className="d-flex justify-content-between font-size-15">
                <span className="text-muted fw-bold">Valor Total:</span>
                <strong className="text-primary fw-bold">
                  {Number(selectedInvoice.amount || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                </strong>
              </div>
            </div>
            <Button
              color="secondary"
              className="w-100 py-2 font-size-13"
              onClick={() => setView("main")}
            >
              Voltar para as faturas
            </Button>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="border-top-0 pt-0">
        {view === "main" && (
          <Button color="secondary" className="w-100 py-2 font-size-13" onClick={toggle}>
            Fechar
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default TenantAccessBlockedModal;
